import posthog from 'posthog-js'

type AnalyticsValue = string | number | boolean
export type AnalyticsProperties = Record<string, AnalyticsValue>

let isInitialized = false

const discardedEventProperties = [
  '$browser_version',
  '$device',
  '$gclid',
  '$dclid',
  '$fbclid',
  '$msclkid',
  '$os_version',
  '$screen_height',
  '$screen_width',
  '$title',
  '$utm_campaign',
  '$utm_content',
  '$utm_medium',
  '$utm_source',
  '$utm_term',
  '$viewport_height',
  '$viewport_width',
]

// Only whitelisted subpages are kept so that dynamic segments (ids, uuids) are never sent
const TRACKED_SUBPAGES_BY_SECTION: Record<string, string[]> = {
  monitoring: ['map', 'sites', 'ssids', 'infras', 'topology'],
}

const getPageSection = (pathname: string): string => {
  const [section, subpage] = pathname.split('?')[0].split('#')[0].split('/').filter(Boolean)

  if (!section) return '/'

  const isTrackedSubpage = Boolean(subpage) && Boolean(TRACKED_SUBPAGES_BY_SECTION[section]?.includes(subpage))
  if (isTrackedSubpage) return `/${section}/${subpage}`

  return `/${section}`
}

const getSafeUrl = (url: unknown): unknown => {
  if (typeof url !== 'string') return url

  try {
    const parsedUrl = new URL(url, window.location.origin)
    return `${parsedUrl.origin}${getPageSection(parsedUrl.pathname)}`
  } catch {
    return getPageSection(url)
  }
}

const getReferrerOrigin = (url: unknown): unknown => {
  if (typeof url !== 'string' || !url) return url

  try {
    return new URL(url, window.location.origin).origin
  } catch {
    return ''
  }
}

const getSafePath = (path: unknown): unknown => (
  typeof path === 'string' ? getPageSection(path) : path
)

const getPageContext = (): AnalyticsProperties => ({
  page_path: getPageSection(window.location.pathname),
  platform: 'web',
})

export const trackEvent = (eventName: string, properties: AnalyticsProperties = {}) => {
  if (!isInitialized) return

  posthog.capture(eventName, {
    ...getPageContext(),
    ...properties,
  })
}

export const initializeProductAnalytics = () => {
  const projectKey = process.env.REACT_APP_POSTHOG_KEY

  if (!projectKey || isInitialized) return

  posthog.init(projectKey, {
    api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com',
    autocapture: false,
    capture_exceptions: false,
    capture_heatmaps: false,
    capture_pageview: false,
    capture_pageleave: false,
    cookieless_mode: 'always',
    disable_session_recording: true,
    disable_surveys: true,
    disable_web_experiments: true,
    person_profiles: 'never',
    advanced_disable_flags: true,
    before_send: (event) => {
      if (!event) return null

      event.properties.$current_url = getSafeUrl(event.properties.$current_url)
      event.properties.$pathname = getSafePath(event.properties.$pathname)
      event.properties.$referrer = getReferrerOrigin(event.properties.$referrer)
      event.properties.$initial_current_url = getSafeUrl(event.properties.$initial_current_url)
      event.properties.$initial_pathname = getSafePath(event.properties.$initial_pathname)
      event.properties.$initial_referrer = getReferrerOrigin(event.properties.$initial_referrer)

      discardedEventProperties.forEach((property) => delete event.properties[property])

      return event
    },
  })

  isInitialized = true
}

export const trackPageView = (pathname: string) => {
  const pagePath = getPageSection(pathname)

  trackEvent('$pageview', {
    page_path: pagePath,
    $current_url: `${window.location.origin}${pagePath}`,
  })
}

export const getDateRangeKind = (startDate: Date | null, endDate: Date | null): string => {
  if (startDate && endDate) return 'bounded'
  if (startDate) return 'start_only'
  if (endDate) return 'end_only'

  return 'empty'
}

export { getPageSection }
